import type { MenuProps, PopconfirmProps, RadioChangeEvent, TablePaginationConfig } from "antd";
import { Button, Checkbox, Dropdown, Flex, Form, Input, message, Modal, Radio, Table, Tag, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { ApiService } from "api/api.service";
import PageTitle from "components/PageTitle";
import TextNumeric from "components/TextNumeric";
import { BlogStatusTypes, BlogVisibilityTypes } from "dtos/blog.dto";
import { CustomerStoryDto, CustomerStorySchemaDto } from "dtos/customerStory.dto";
import DashboardLayout from "layout/DashboardLayout";
import { useEffect, useState } from "react";
import {
  TbChevronLeft,
  TbChevronRight,
  TbColumns3,
  TbDots,
  TbEye,
  TbFlare,
  TbLoader2,
  TbPlus,
  TbSearch,
  TbStatusChange,
} from "react-icons/tb";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { BLOG_STATUS_COLOURS, BLOG_VISIBILITY_COLOURS } from "utils/constants";
import { formatDate, formatDateTime } from "utils/functions";

const BLOG_VISIBILITY_LABELS = {
  [BlogVisibilityTypes.HIDDEN]: "Hidden",
  [BlogVisibilityTypes.MEMBERSHIP_ONLY]: "Membership Only",
  [BlogVisibilityTypes.OPEN_TO_ALL]: "Open to All",
  [BlogVisibilityTypes.UNPUBLISHED]: "Unpublished",
};

type PopulatedCustomerStoryDto = NonNullable<
  ReturnType<typeof ApiService.customerStory.useGetAllPaginated>["data"]
>["data"][number];

const CustomerStoriesAll = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
    categories: string[];
    status?: string[];
    visibility?: string[];
    sorting: number;
  }>({
    page: Number(searchParams.get("page")) === 0 ? 1 : (Number(searchParams.get("page")) ?? 1),
    limit: Number(searchParams.get("limit")) === 0 ? 10 : (Number(searchParams.get("limit")) ?? 10),
    categories: [],
    status: [],
    visibility: [],
    sorting: -1,
  });
  const [selectedVisibility, setSelectedVisibility] = useState({ init: "", later: "" });
  const [selectedStatus, setSelectedStatus] = useState({ init: "", later: "" });
  const [selectedFeature, setSelectedFeature] = useState({ init: "", later: "" });
  const [openVisibilityModal, setOpenVisibilityModal] = useState(false);
  const [openStatusModal, setStatusModal] = useState(false);
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);
  const [openFeatureModal, setOpenFeatureModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomerStory, setSelectedCustomerStory] = useState("");
  const customerStoryList = ApiService.customerStory.useGetAllPaginated({
    page: filters.page,
    limit: filters.limit,
    categories: filters.categories,
    status: filters.status,
    visibility: filters.visibility,
    searchString: searchTerm,
    sortOrder: filters.sorting,
  });
  const getAllCategories = ApiService.category.useGetAllPaginated({ page: 1, limit: 10 });
  const deleteCustomerStory = ApiService.customerStory.useDeleteBySlugOrId();
  const updateStatus = ApiService.blog.useUpdateStatus();
  const updateVisibility = ApiService.blog.useUpdateVisibility();
  const updateFeature = ApiService.blog.useUpdateFeature();
  const [categoryForm] = Form.useForm();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const items: MenuProps["items"] = [
    {
      label: "Change Visibility",
      key: "1",
      icon: <TbEye />,
    },
    {
      label: "Change Status",
      key: "2",
      icon: <TbStatusChange />,
    },
    {
      label: "Feature",
      key: "3",
      icon: <TbFlare />,
    },
  ];

  const handleRowUpdate = (rowId: string) => {
    setHighlightedRow(rowId);

    // Clear the highlight after 1 second
    setTimeout(() => {
      setHighlightedRow(null);
    }, 1000);
  };

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    message.info("Click on left button.");
    console.log("click left button", e);
  };
  const handleDropDownSelect = (visibility: string, status: string, id: string, feature: boolean) => {
    // console.log(visibility, status);
    setSelectedVisibility({ init: visibility, later: visibility });
    setSelectedStatus({ init: status, later: status });
    setSelectedCustomerStory(id);
    const featText = feature ? "Yes" : "No";
    setSelectedFeature({ init: featText, later: featText });
  };
  const formatTypes = (value: string) => {
    const formattedvalue = value.replace(/_/g, " ");
    return formattedvalue[0].toUpperCase() + formattedvalue.slice(1).toLowerCase();
  };
  const handleMenuClick: MenuProps["onClick"] = (e) => {
    // message.info("Click on menu item.");
    e.key === "1" ? setOpenVisibilityModal(true) : "";
    e.key === "2" ? setStatusModal(true) : "";
    e.key === "3" ? setOpenFeatureModal(true) : "";
  };
  const menuProps = {
    items,
    onClick: handleMenuClick,
  };
  const handleVisibilityChange = () => {
    updateVisibility.mutate(
      { id: selectedCustomerStory, visiblity: selectedVisibility.later },
      {
        onSuccess: () => {
          message.success("Visibility updated successfully");
          customerStoryList.refetch();
          setOpenVisibilityModal(false);
          handleRowUpdate(selectedCustomerStory);
        },
      },
    );
  };
  const handleStatusChanged = () => {
    updateStatus.mutate(
      { id: selectedCustomerStory, status: selectedStatus.later },
      {
        onSuccess: () => {
          message.success("Status updated successfully");
          customerStoryList.refetch();
          setStatusModal(false);
          handleRowUpdate(selectedCustomerStory);
        },
      },
    );
  };

  const handleFeatureChange = () => {
    updateFeature.mutate(
      { id: selectedCustomerStory, feature: selectedFeature.later === "Yes" },
      {
        onSuccess: () => {
          message.success("Featured status changed successfully");
          customerStoryList.refetch();
          setOpenFeatureModal(false);
          handleRowUpdate(selectedCustomerStory);
        },
      },
    );
  };
  const confirm = (id: string) => {
    console.log("delete");
    deleteCustomerStory.mutate(id, {
      onSuccess: () => {
        message.success("Customer story deleted successfully");
        customerStoryList.refetch();
      },
    });
  };

  const handleCategoryChange = (value: string[]) => {
    setFilters({ ...filters, categories: value });
  };

  const handleFilterByCategory = () => {
    setFilters((prev) => ({ ...prev, categories: selectedCategories }));
  };

  const handleSearch = () => {
    customerStoryList.refetch();
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    keyFilters: Record<string, any[] | null>,
    sorter: any,
  ) => {
    const statusFilters = keyFilters.status ?? [];
    const visibilityFilters = keyFilters.visiblity ?? [];
    const sort = sorter.order ? (sorter.order === "ascend" ? 1 : -1) : 1;
    console.log(sort);

    setFilters({
      page: pagination.current ?? 1,
      limit: pagination.pageSize ?? 10,
      status: statusFilters,
      visibility: visibilityFilters,
      categories: filters.categories,
      sorting: sort,
    });
  };

  const cancel: PopconfirmProps["onCancel"] = (e) => {};

  const [columnsHidden, setColumnsHidden] = useState<{ [k in keyof CustomerStorySchemaDto]?: boolean }>({
    comments: true,
    updatedAt: true,
    createdAt: true,
    tags: true,
  });

  const columns: ColumnsType<PopulatedCustomerStoryDto> = [
    {
      key: "customerStory",
      title: "Customer Story",
      width: 400,
      fixed: "left",
      // ellipsis: true,
      render: (record: PopulatedCustomerStoryDto) => {
        return (
          <div>
            <Typography.Paragraph className="!m-0" type="secondary">
              <Link className="font-semibold hover:!underline" to={`/dashboard/story/${record.slug}`}>
                {record.title}
              </Link>
            </Typography.Paragraph>
          </div>
        );
      },
    },
    {
      key: "published_at",
      dataIndex: "published_at",
      title: "Published at",
      width: 200,
      render: (_, record: PopulatedCustomerStoryDto) => {
        return (
          <TextNumeric>
            <Typography.Text type="secondary">
              <small>{record?.published_at ? formatDateTime(record?.published_at) : "-"}</small>
            </Typography.Text>
          </TextNumeric>
        );
      },
    },
    {
      key: "status",
      dataIndex: "status",
      title: "Status",
      width: 150,
      render: (status: PopulatedCustomerStoryDto["status"]) => {
        return <Tag color={BLOG_STATUS_COLOURS[status]}>{status[0] + status.slice(1).toLocaleLowerCase()}</Tag>;
      },
    },
    {
      key: "visiblity",
      dataIndex: "visiblity",
      title: "Visibility",
      width: 200,
      render: (value: string) => {
        const visibilityType = value as BlogVisibilityTypes;
        const type = visibilityType.replace(/_/g, " ");
        return <Tag color={BLOG_VISIBILITY_COLOURS[visibilityType]}>{BLOG_VISIBILITY_LABELS[visibilityType]}</Tag>;
      },
    },
    {
      title: "Categories",
      key: "categories",
      width: 250,
      render: (text, record) => <>{record?.categories?.map((category, i) => <Tag key={i}>{category.title}</Tag>)}</>,
    },
    {
      title: "Tags",
      key: "tags",
      width: 250,
      render: (text, record) => (
        <Typography.Text type="secondary" className="text-xs">
          {record?.tags?.map((tag) => `#${tag}`).join(" ")}
        </Typography.Text>
      ),
    },
    {
      title: "Featured",
      key: "featured",
      width: 150,
      render: (text, record) => <>{record.featured ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>}</>,
    },
    {
      title: "Author",
      dataIndex: "author",
      key: "author",
      render: (author) => <span>{author.name}</span>,
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      render: (comments) => comments.length,
      width: 120,
    },
    {
      key: "createdAt",
      dataIndex: "createdAt",
      title: "Created",
      width: 150,
      // ellipsis: true,
      render: (createdAt: PopulatedCustomerStoryDto["createdAt"]) => {
        return (
          <TextNumeric>
            <Typography.Text type="secondary">
              <small>{createdAt ? formatDate(createdAt) : "-"}</small>
            </Typography.Text>
          </TextNumeric>
        );
      },
    },
    {
      key: "updatedAt",
      dataIndex: "updatedAt",
      title: "Updated",
      width: 150,
      // ellipsis: true,
      render: (updatedAt: PopulatedCustomerStoryDto["updatedAt"]) => {
        return (
          <TextNumeric>
            <Typography.Text type="secondary">
              <small>{updatedAt ? formatDate(updatedAt) : "-"}</small>
            </Typography.Text>
          </TextNumeric>
        );
      },
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      // width: 80,
      fixed: "right",
      render: (name: string, record: PopulatedCustomerStoryDto, index: number) => {
        return (
          <Flex justify="center" align="center" gap={2}>
            <Dropdown trigger={["click"]} menu={menuProps}>
              <Button
                onClick={() => handleDropDownSelect(record.visiblity, record.status, record._id, record.featured)}
                icon={<TbDots />}
                type="text"
              />
            </Dropdown>
          </Flex>
        );
      },
    },
  ];
  useEffect(() => {
    if (searchParams.has("cat")) {
      const newCategory = searchParams.get("cat") as string;
      if (!filters.categories.includes(newCategory)) {
        setFilters((prev) => ({ ...prev, categories: [newCategory] }));
      }
    }
  }, [searchParams]);

  useEffect(() => {
    let getSearchData: NodeJS.Timeout | undefined;
    if (searchTerm) {
      getSearchData = setTimeout(() => {
        customerStoryList.refetch();
      }, 2000);
    }

    return () => {
      if (getSearchData) clearTimeout(getSearchData);
    };
  }, [searchTerm]);

  return (
    <DashboardLayout
      headerLeft={
        <Flex gap={12}>
          <Typography.Title level={5} className="!m-0">
            All Customer Stories{" "}
          </Typography.Title>
          <Typography.Title level={5} className="!m-0 !font-bold" type="secondary">
            {customerStoryList.data?.totalObjects}
          </Typography.Title>
        </Flex>
      }
      headerRight={
        <Button
          type="primary"
          onClick={() => {
            navigate(`/dashboard/create-story`);
          }}
          icon={<TbPlus />}
        >
          Create Story
        </Button>
      }
    >
      <PageTitle title="Customer Stories" pageTitle={[{ title: "Customer Stories" }]} />

      <Table
        // className="border border-gray-300 rounded-xl overflow-hidden"
        virtual={false}
        loading={{
          spinning: customerStoryList.isFetching,
          indicator: <TbLoader2 size={48} className="text-brand-500 animate-spin" />,
        }}
        columns={columns
          .map((col) => {
            if (
              Object.keys(columnsHidden).includes(col.key as string) &&
              (columnsHidden as any)[col.key as any] === true
            ) {
              return null;
            } else return col;
          })
          .filter((col) => !!col)}
        dataSource={customerStoryList.data?.data}
        // onChange={handleTableChange}
        rowKey={"_id"} // Add this line
        pagination={{
          hideOnSinglePage: true,
          showQuickJumper: true,
          showPrevNextJumpers: true,
          nextIcon: (
            <Button
              className="ant-pagination-item-link !flex items-center justify-center"
              type="text"
              icon={<TbChevronRight size={20} />}
            />
          ),
          prevIcon: (
            <Button
              className="ant-pagination-item-link !flex items-center justify-center"
              type="text"
              icon={<TbChevronLeft size={20} />}
            />
          ),
          showTitle: true,
          showTotal: (total, range) => {
            return `Showing ${range[0]}-${range[1]} of ${total} stories`;
          },
          showSizeChanger: true,
          current: filters.page,
          total: customerStoryList.data?.totalObjects,
          pageSize: filters.limit,
          onChange: (page, pageSize) => {
            setFilters((prev) => ({ ...prev, page, limit: pageSize, sorting: -1 }));
          },
        }}
        rowClassName={(record) => (record._id === highlightedRow ? "highlighted-row" : "")}
        title={() => {
          return (
            <Form form={categoryForm}>
              <Form.Item<CustomerStoryDto> name={"categories"} style={{ marginBottom: "0px" }}>
                <div className="flex justify-between">
                  <div className="inline-flex gap-2">
                    <Dropdown
                      menu={{
                        items: columns
                          .filter((el) => !["customerStory", "action"].includes(el.key as string))
                          .map((el) => {
                            const key = String(el.key);
                            return {
                              key: key,
                              label: (
                                <div className="flex gap-2" key={el.key}>
                                  <Checkbox checked={!(columnsHidden as any)[key]} />
                                  <span>{el.title?.toString()}</span>
                                </div>
                              ),
                              onClick: () => {
                                setColumnsHidden((prev) => ({ ...prev, [key]: !(prev as any)[key] }));
                              },
                            };
                          }),
                      }}
                    >
                      <Button icon={<TbColumns3 />}>
                        Columns{" "}
                        <Tag bordered={false} className="!m-0 !bg-gray-100 !font-medium">
                          {
                            columns.filter((el) => {
                              if (["customerStory", "action"].includes(el.key as string)) return false;
                              else if ((el.key as string) in (columnsHidden as any)) return false;
                              else return true;
                            }).length
                          }
                        </Tag>
                      </Button>
                    </Dropdown>
                    {/* <Button icon={<TbSortDescending />}>Sort</Button>
                    <Button icon={<LuListFilter />}>Filter</Button> */}
                  </div>
                  <Input
                    placeholder="Search stories"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: "200px" }}
                    prefix={<TbSearch className="stroke-gray-400" />}
                    onPressEnter={handleSearch}
                    allowClear
                    onClear={handleSearch}
                  />
                </div>
              </Form.Item>
            </Form>
          );
        }}
        scroll={{ y: "calc(100vh - 64px - 48px - 64px - 64px)", x: "max-content" }}
      />

      <Modal
        title={`Change visibility`}
        open={openVisibilityModal}
        onOk={handleVisibilityChange}
        okText={"Update"}
        onCancel={() => setOpenVisibilityModal(false)}
        okButtonProps={{
          disabled: selectedVisibility.init === selectedVisibility.later,
          loading: updateVisibility.isPending,
        }}
      >
        <Radio.Group
          value={selectedVisibility.later}
          onChange={(e: RadioChangeEvent) => setSelectedVisibility({ ...selectedVisibility, later: e.target.value })}
          options={Object.values(BlogVisibilityTypes).map((type) => ({
            label: formatTypes(type),
            value: type,
          }))}
        />
      </Modal>

      <Modal
        title={`Change Status`}
        open={openStatusModal}
        okText={"Update"}
        onOk={handleStatusChanged}
        onCancel={() => setStatusModal(false)}
        okButtonProps={{ disabled: selectedStatus.init === selectedStatus.later, loading: updateStatus.isPending }}
      >
        <Radio.Group
          value={selectedStatus.later}
          onChange={(e: RadioChangeEvent) => setSelectedStatus({ ...selectedStatus, later: e.target.value })}
          options={Object.values(BlogStatusTypes).map((type) => ({
            label: formatTypes(type),
            value: type,
          }))}
        />
      </Modal>
      <Modal
        title={`Do you want this customer story to be featured?`}
        open={openFeatureModal}
        onOk={handleFeatureChange}
        okText={"Update"}
        onCancel={() => setOpenFeatureModal(false)}
        okButtonProps={{
          disabled: selectedFeature.init === selectedFeature.later,
          loading: updateFeature.isPending,
        }}
      >
        <Radio.Group
          value={selectedFeature.later}
          onChange={(e: RadioChangeEvent) => setSelectedFeature({ ...selectedFeature, later: e.target.value })}
          options={[
            { label: "Yes", value: "Yes" },
            { label: "No", value: "No" },
          ]}
        />
      </Modal>
    </DashboardLayout>
  );
};

export default CustomerStoriesAll;
